/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { notificationsRuntimeService } from '../../services/notifications.service';
import { AttachmentField, type AttachmentValue } from './attachment-field';

/** What WhatsApp accepts across its four media kinds. */
const MEDIA_ACCEPT =
  'image/png,image/jpeg,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,'
  + '.txt,.csv,audio/*,video/mp4,video/3gpp';

/**
 * What a WhatsApp message is, not just what it says.
 *
 * WhatsApp is the one channel where a template is not a string: `messageType`
 * decides whether the gateway gets text, an attachment, a pin or a shared
 * contact. The stored shape is the live notification master's, field for field,
 * so the live dispatcher — which already routes on every one of these — can send
 * it unchanged, and a master moved between the two systems is not re-authored.
 *
 * A message is always authored here. A pre-approved Meta template was the
 * alternative, and it is gone: every stored template was freeform, and offering
 * both is how the live system ended up with a message typed into the template
 * NAME field, which saved cleanly and sent blank.
 *
 * The fields shown follow the type — offering every field at once is what made
 * that mistake available in the first place.
 */
export interface WhatsAppBlock {
  messageType?: string;
  title?: string;
  body?: string;
  caption?: string;
  footer?: string;
  media?: { source?: string; fileId?: string; mediaType?: string; filename?: string } | null;
  latitude?: string;
  longitude?: string;
  address?: string;
  contact?: string;
}

interface Props {
  value: WhatsAppBlock;
  onChange: (next: WhatsAppBlock) => void;
  /** Declared parameters, offered in the attachment field's Static mode. */
  parameterNames?: string[];
  testId?: string;
}

/** Types whose content IS the file, so they need an attachment. */
const MEDIA_TYPES = ['image', 'document', 'audio', 'video'];
const MESSAGE_TYPES = ['text', ...MEDIA_TYPES, 'location', 'contact'].map((t) => ({ value: t, label: t }));

/** Keys that belong to one shape and would be stale noise under another. */
const MEDIA_KEYS = ['media', 'caption'];
const LOCATION_KEYS = ['latitude', 'longitude', 'address'];
const CONTACT_KEYS = ['contact'];

const without = (block: WhatsAppBlock, keys: string[]): WhatsAppBlock => {
  const next: any = { ...block };
  keys.forEach((k) => delete next[k]);
  return next;
};

const WhatsAppTemplateFields = ({ value, onChange, parameterNames = [], testId = 'WhatsAppTemplate' }: Props) => {
  const messageType = value.messageType || 'text';
  const isMedia = MEDIA_TYPES.includes(messageType);

  const set = (patch: Partial<WhatsAppBlock>) => onChange({ ...value, ...patch });

  // A change of type drops the keys of the shape being left. A stale `media`
  // under a text message is content the author cannot see and did not intend.
  const changeType = (next: string) => {
    let base = without(value, [...MEDIA_KEYS, ...LOCATION_KEYS, ...CONTACT_KEYS]);
    if (MEDIA_TYPES.includes(next)) base = { ...base, media: { source: 'url', mediaType: next } };
    onChange({ ...base, messageType: next });
  };

  /** media.fileId holds the reference however it was set. A filename is only
   *  written by an upload, which is what tells an uploaded file from a pasted
   *  link — both are plain strings by the time they are stored. */
  const mediaValue = (): AttachmentValue => {
    const ref = (value.media?.fileId || '').trim();
    if (!ref) return { kind: 'none' };
    const placeholder = ref.match(/^\{([^{}]+)\}$/);
    if (placeholder) return { kind: 'parameter', name: placeholder[1].trim() };
    if (value.media?.filename) {
      return {
        kind: 'file',
        file: {
          fileId: ref,
          filename: value.media.filename,
          mimeType: value.media.mediaType || messageType,
          sizeBytes: 0,
          url: null,
        },
      };
    }
    return { kind: 'url', url: ref };
  };

  const setMedia = (next: AttachmentValue) => {
    const base = { source: 'url', mediaType: messageType };
    if (next.kind === 'parameter') { set({ media: { ...base, fileId: `{${next.name}}` } }); return; }
    if (next.kind === 'url') { set({ media: { ...base, fileId: next.url } }); return; }
    if (next.kind === 'file') {
      set({ media: {
        source: 'url',
        fileId: next.file.fileId,
        // The backend derives the real kind from the bytes; trust it over the
        // message type, which is only what the author said they were sending.
        mediaType: (next.file as { mediaType?: string }).mediaType || messageType,
        filename: next.file.filename,
      } });
      return;
    }
    set({ media: base });
  };

  const bodyLabel = messageType === 'location' ? 'Body (joined into the pin label)'
    : messageType === 'contact' ? 'Body (not sent with a contact card)'
    : isMedia ? 'Body (optional for a media message)'
    : (value.title || '').trim() ? 'Body' : 'Body *';

  const field = (label: string, key: keyof WhatsAppBlock, opts: { area?: boolean; hint?: string; placeholder?: string } = {}) => (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      {opts.area ? (
        <InputTextarea value={(value[key] as string) || ''} rows={4} className="w-full"
          placeholder={opts.placeholder} data-testid={testId + '.' + key}
          onChange={(e) => set({ [key]: e.target.value } as any)} />
      ) : (
        <InputText value={(value[key] as string) || ''} className="w-full"
          placeholder={opts.placeholder} data-testid={testId + '.' + key}
          onChange={(e) => set({ [key]: e.target.value } as any)} />
      )}
      {opts.hint ? <small className="text-xs text-gray-400">{opts.hint}</small> : null}
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-3" data-testid={testId}>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Message type</label>
        <Dropdown value={messageType} options={MESSAGE_TYPES} optionLabel="label" optionValue="value"
          className="w-full" data-testid={testId + '.messageType'} onChange={(e) => changeType(e.value)} />
      </div>

      {isMedia && (
        <div className="flex flex-col gap-1">
          {/* The same Static / Dynamic control email, push and web push use. A
              WhatsApp media reference lives in media.fileId whichever way it was
              set — a URL, a {placeholder}, or the id an upload returns — so only
              the surrounding media object differs from the other channels. */}
          <AttachmentField
            label="Attachment *"
            value={mediaValue()}
            parameterNames={parameterNames}
            accept={MEDIA_ACCEPT}
            upload={notificationsRuntimeService.uploadWhatsappMedia}
            uploadWarning={(up) => (up.url ? null
              : 'Uploaded, but this deployment has no public base URL, so WhatsApp cannot fetch it. '
                + 'Set it under Channel Integrations and upload again.')}
            testId={testId + '.media'}
            onChange={setMedia}
          />
          {messageType === 'document' && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Filename shown to the recipient</label>
              <InputText value={value.media?.filename || ''} className="w-full"
                placeholder="report.pdf or {filename}" data-testid={testId + '.media.filename'}
                onChange={(e) => set({ media: { ...(value.media || { source: 'url', mediaType: 'document' }), filename: e.target.value } })} />
            </div>
          )}
        </div>
      )}

      {messageType === 'location' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {field('Latitude *', 'latitude', { placeholder: '16.3067 or {siteLat}' })}
          {field('Longitude *', 'longitude', { placeholder: '80.4365 or {siteLng}' })}
          {field('Address label', 'address', { placeholder: 'Gate 3, Guntur — max 300 chars' })}
          <small className="md:col-span-3 text-xs text-gray-400">
            A coordinate may be fixed or a {'{placeholder}'} from the payload. A pin is one message and
            carries no attachment — the title, this label, the body and the footer are joined into the
            text shown under it.
          </small>
        </div>
      )}

      {messageType === 'contact' && field('Contact to share *', 'contact', {
        placeholder: '919999999999 or {officerPhone}',
        hint: 'The gateway shares an existing WhatsApp contact by number — not a vCard.',
      })}

      {field('Title', 'title', {
        placeholder: 'Heading — supports {placeholders}',
        hint: 'Sent in bold above the text. WhatsApp has no title of its own, so it is composed in.',
      })}
      {field(bodyLabel, 'body', { area: true, placeholder: 'Message text — supports {placeholders}' })}
      {isMedia && field('Caption', 'caption', {
        placeholder: 'Shown with the attachment — supports {placeholders}',
        hint: 'Composed like a body: heading, caption, footer.',
      })}
      {field('Footer', 'footer', { placeholder: 'Sent in italics below — optional' })}
    </div>
  );
};

export default WhatsAppTemplateFields;
